import api from "./api";

/**
 * Basic service to interact with backend directly without local storage concerns.
 */
class RegularAppService {
  // Map to deduplicate in-flight requests per identical query
  static inflight = new Map();
  /**
   * Fetch a page from the paginated backend endpoint.
   * @param {Object} options
   * @param {string} options.collection
   * @param {number} [options.page]
   * @param {number} [options.limit]
   * @param {string} [options.search]
   * @param {string[]} [options.fields]
   * @param {string} [options.sortBy]
   * @param {"asc"|"desc"} [options.sortOrder]
   * @param {object} [options.filter]
   * @returns {Promise<object>} Returns backend response as-is
   */
  async getPaginated(options = {}) {
    const { collection } = options;
    const hasCollection =
      typeof collection === "string" && collection.trim().length > 0;
    if (!hasCollection) throw new Error("collection is required");

    const params = {};

    const defaultLimitFromEnv = Number(
      process.env.REACT_APP_DEFAULT_PAGE_LIMIT
    );
    const isEnvLimitValid =
      Number.isFinite(defaultLimitFromEnv) && defaultLimitFromEnv > 0;
    const defaultLimit = isEnvLimitValid ? defaultLimitFromEnv : 20;

    const hasPage = Number.isFinite(Number(options.page));
    params.page = hasPage ? Number(options.page) : 1;

    const hasLimit = Number.isFinite(Number(options.limit));
    params.limit = hasLimit ? Number(options.limit) : defaultLimit;

    const hasSearch =
      typeof options.search === "string" && options.search.trim().length > 0;
    if (hasSearch) params.search = options.search.trim();

    const hasFields =
      Array.isArray(options.fields) && options.fields.length > 0;
    if (hasFields) params.fields = options.fields.join(",");

    const hasSortBy =
      typeof options.sortBy === "string" && options.sortBy.trim().length > 0;
    params.sortBy = hasSortBy ? options.sortBy.trim() : "updatedAt";

    const hasSortOrder =
      typeof options.sortOrder === "string" &&
      options.sortOrder.trim().length > 0;
    params.sortOrder = hasSortOrder ? options.sortOrder.trim() : "desc";

    const hasFilter = options.filter && typeof options.filter === "object";
    if (hasFilter) params.filter = JSON.stringify(options.filter);

    if (typeof options.exact === "boolean") params.exact = options.exact;

    const endpoint = `/regular-paginated-app/${collection}`;
    const key = `${endpoint}:${JSON.stringify(params)}`;
    // Return existing in-flight request if present
    if (RegularAppService.inflight.has(key)) {
      return await RegularAppService.inflight.get(key);
    }

    const promise = api
      .get(endpoint, { params })
      .then((response) => {
        return response;
      })
      .finally(() => {
        // Clean up in-flight map
        RegularAppService.inflight.delete(key);
      });

    RegularAppService.inflight.set(key, promise);
    const response = await promise;
    return response;
  }

  /**
   * Fetch and aggregate all pages until the last one.
   * @param {Object} options
   * @param {string} options.collection
   * @param {number} [options.page]
   * @param {number} [options.limit]
   * @param {string} [options.search]
   * @param {string[]} [options.fields]
   * @param {string} [options.sortBy]
   * @param {"asc"|"desc"} [options.sortOrder]
   * @param {object} [options.filter]
   * @returns {Promise<Array>} Aggregated items across pages
   */
  async getAll(options = {}) {
    const defaulted = { limit: 100, page: 1, ...options };
    const items = [];
    let currentPage = Number(defaulted.page);
    for (;;) {
      const isValidPage = Number.isFinite(currentPage) && currentPage > 0;
      const pageToUse = isValidPage ? currentPage : 1;
      const result = await this.getPaginated({ ...defaulted, page: pageToUse });
      const hasItems = result && Array.isArray(result.items);
      if (hasItems) items.push(...result.items);
      const hasNext = Boolean(result?.hasNextPage);
      if (!hasNext) break;
      currentPage += 1;
    }
    return items;
  }

  /**
   * Upsert a single document using the existing backend app endpoint.
   * @param {Object} options
   * @param {string} options.collection
   * @param {object} options.data
   * @returns {Promise<object>} Backend response as-is
   */
  async upsert(options = {}) {
    const { collection, data } = options;
    const hasCollection =
      typeof collection === "string" && collection.trim().length > 0;
    if (!hasCollection) throw new Error("collection is required");
    const hasData = data && typeof data === "object";
    if (!hasData) throw new Error("data is required");
    const endpoint = `/app/${collection}`;
    const response = await api.post(endpoint, data);
    return response;
  }
}

const regularAppService = new RegularAppService();
export default regularAppService;
